import { useCallback, useEffect, useState } from 'react';
import { accountService } from '../services/accountService';

const EMPTY_CONTACT = {
  name: '',
  designation: '',
  department: '',
  email: '',
  mobile: '',
  singlePointOfContact: 'no',
};

const EMPTY_FORM = {
  name: '',
  email: '',
  phone: '',
  address: '',
  reference_name: '',
  reference_phone: '',
  reference_email: '',
  comments: '',
};

function validateDetails(form) {
  const errors = {};
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const phoneRegex = /^[0-9]{10,13}$/;

  if (!form.name.trim()) {
    errors.name = 'Business function name is required';
  }
  if (form.email && !emailRegex.test(form.email)) {
    errors.email = 'Enter a valid email address';
  }
  if (form.phone && !phoneRegex.test(form.phone.replace(/\D/g, ''))) {
    errors.phone = 'Phone must be 10 to 13 digits';
  }
  if (form.reference_email && !emailRegex.test(form.reference_email)) {
    errors.reference_email = 'Enter a valid reference email';
  }
  if (form.reference_phone && !phoneRegex.test(form.reference_phone.replace(/\D/g, ''))) {
    errors.reference_phone = 'Reference phone must be 10 to 13 digits';
  }

  return errors;
}

function validateContacts(contacts) {
  const errors = {};
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const phoneRegex = /^[0-9]{10}$/;

  contacts.forEach((contact, index) => {
    const rowErrors = {};
    if (!contact.name?.trim()) {
      rowErrors.name = 'Name is required';
    }
    if (contact.email && !emailRegex.test(contact.email)) {
      rowErrors.email = 'Enter a valid email address';
    }
    if (!String(contact.mobile || '').trim()) {
      rowErrors.mobile = 'Mobile number is required';
    } else if (!phoneRegex.test(String(contact.mobile || ''))) {
      rowErrors.mobile = 'Mobile must be 10 digits';
    }

    if (Object.keys(rowErrors).length > 0) {
      errors[index] = rowErrors;
    }
  });

  return errors;
}

export default function useBusinessFunctionCreateForm({ open, organisationId, onCreated, onClose }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [contacts, setContacts] = useState([EMPTY_CONTACT]);
  const [detailErrors, setDetailErrors] = useState({});
  const [contactErrors, setContactErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    if (open) {
      setForm(EMPTY_FORM);
      setContacts([EMPTY_CONTACT]);
      setDetailErrors({});
      setContactErrors({});
      setIsSubmitting(false);
      setSubmitError('');
    }
  }, [open]);

  const updateField = useCallback((field, value) => {
    const sanitized =
      field.includes('phone') && typeof value === 'string'
        ? value.replace(/\D/g, '')
        : value;

    setForm((prev) => ({ ...prev, [field]: sanitized }));
    setDetailErrors((prev) => ({ ...prev, [field]: '' }));
    setSubmitError('');
  }, []);

  const updateContact = useCallback((index, field, value) => {
    const sanitized = field === 'mobile' && typeof value === 'string'
      ? value.replace(/\D/g, '')
      : value;

    setContacts((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: sanitized };
      return next;
    });

    setContactErrors((prev) => {
      if (!prev[index]) return prev;
      const next = { ...prev };
      if (next[index]) {
        next[index] = { ...next[index], [field]: '' };
      }
      return next;
    });
    setSubmitError('');
  }, []);

  const addContact = useCallback(() => {
    setContacts((prev) => [...prev, { ...EMPTY_CONTACT }]);
  }, []);

  const removeContact = useCallback((index) => {
    setContacts((prev) => prev.filter((_, i) => i !== index));
    setContactErrors((prev) => {
      const next = {};
      Object.keys(prev).forEach((key) => {
        const keyNum = Number(key);
        if (keyNum < index) next[keyNum] = prev[keyNum];
        if (keyNum > index) next[keyNum - 1] = prev[keyNum];
      });
      return next;
    });
  }, []);

  const submit = useCallback(async () => {
    const nextDetailErrors = validateDetails(form);
    const nextContactErrors = validateContacts(contacts);
    const hasDetailErrors = Object.keys(nextDetailErrors).length > 0;
    const hasContactErrors = Object.keys(nextContactErrors).length > 0;

    setDetailErrors(nextDetailErrors);
    setContactErrors(nextContactErrors);

    if (hasDetailErrors || hasContactErrors) {
      return { success: false };
    }

    if (!organisationId) {
      const message = 'Select an organisation before creating a business function.';
      setSubmitError(message);
      return { success: false, message };
    }

    setIsSubmitting(true);
    setSubmitError('');

    try {
      const response = await accountService.createAccount({
        ...form,
        name: form.name.trim(),
        organisation: organisationId,
        contacts,
      });

      if (response?.status === 200 && response?.data?.status !== false) {
        const createdAccount = response.data?.data ?? response.data;
        await onCreated?.(createdAccount);
        onClose?.();
        return { success: true, data: createdAccount };
      }

      const errorPayload = response?.data?.data ?? response?.data;
      const message =
        errorPayload?.general_error?.name?.[0] ||
        errorPayload?.general_error ||
        errorPayload?.name?.[0] ||
        errorPayload?.message ||
        'Unable to create business function';

      setSubmitError(typeof message === 'string' ? message : 'Unable to create business function');
      return { success: false, message };
    } catch (err) {
      const message =
        err?.response?.data?.name?.[0] ||
        err?.response?.data?.message ||
        err?.message ||
        'Unable to create business function';
      setSubmitError(message);
      return { success: false, message };
    } finally {
      setIsSubmitting(false);
    }
  }, [contacts, form, onClose, onCreated, organisationId]);

  return {
    form,
    contacts,
    detailErrors,
    contactErrors,
    isSubmitting,
    submitError,
    updateField,
    updateContact,
    addContact,
    removeContact,
    submit,
  };
}
